import { Box, TextField, Typography, Autocomplete, CircularProgress, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import axiosService from 'utils/axios';
import { Importer, Company, Person } from 'types/entities/types';
import ModalPopup from '../../../components/Modal/ModalPopup';
import AddOrEditPeople from '../people/AddOrEditPeople';
import AddOrEditCompany from '../companies/AddOrEditCompany';
import { User } from 'types/auth/types';
import AddOrEditUser from 'pages/users/list/AddOrEditUser';

interface Props {
  importer?: Importer;
  onSave: (importer: Importer) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface ImporterEntity {
  id: string;
  name: string;
}

interface ImporterCreateUpdateError {
  error: string;
}

export default function AddOrEditImporter(props: Props) {
  const { importer, onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ImporterCreateUpdateError | null>();

  const [personOptions, setPersonOptions] = useState<ImporterEntity[]>([]);
  const [isPersonOptionsLoading, setIsPersonOptionsLoading] = useState(false);

  const [companyOptions, setCompanyOptions] = useState<ImporterEntity[]>([]);
  const [isCompanyOptionsLoading, setIsCompanyOptionsLoading] = useState(false);

  const [userOptions, setUserOptions] = useState<ImporterEntity[]>([]);
  const [isUserOptionsLoading, setIsUserOptionsLoading] = useState(false);

  const [personTextInput, setPersonTextInput] = useState<string>();
  const [addNewPersonModalOpen, setAddNewPersonModalOpen] = useState(false);
  const [personChoice, setPersonChoice] = useState<ImporterEntity | null>(
    importer?.person
      ? {
          id: importer.person.id,
          name: `${importer.person.first_name} ${importer.person.last_name}`,
        }
      : null
  );

  const [companyTextInput, setCompanyTextInput] = useState<string>();
  const [addNewCompanyModalOpen, setAddNewCompanyModalOpen] = useState(false);
  const [companyChoice, setCompanyChoice] = useState<ImporterEntity | null>(
    importer?.company
      ? {
          id: importer.company.id,
          name: importer.company.name,
        }
      : null
  );

  // User options
  const [userTextInput, setUserTextInput] = useState<string>();
  const [addNewUserModalOpen, setAddNewUserModalOpen] = useState(false);
  const [userChoice, setUserChoice] = useState<ImporterEntity | null>(
    importer?.user
      ? {
          id: importer.user.id,
          name: `${importer.user.first_name} ${importer.user.last_name}`.trim(),
        }
      : null
  );

  const [notesInput, setNotesInput] = useState(importer?.notes || '');

  const getPersonList = useCallback((onSuccess?: () => void) => {
    setIsPersonOptionsLoading(true);
    axiosService
      .get(`/entities/person/`)
      .then(res => {
        const names: ImporterEntity[] = [];
        res.data.forEach((person: Person) =>
          names.push({ id: person.id, name: `${person.first_name} ${person.last_name}` })
        );
        setPersonOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsPersonOptionsLoading(false));
  }, []);

  const getCompaniesList = useCallback((onSuccess?: () => void) => {
    setIsCompanyOptionsLoading(true);
    axiosService
      .get(`/entities/company/`)
      .then(res => {
        const names: ImporterEntity[] = [];
        res.data.forEach((company: Company) => names.push({ id: company.id, name: company.name }));
        setCompanyOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsCompanyOptionsLoading(false));
  }, []);

  const getUsersList = useCallback((onSuccess?: () => void) => {
    setIsUserOptionsLoading(true);
    axiosService
      .get(`/user/`)
      .then(res => {
        const names: ImporterEntity[] = [];
        res.data.forEach((user: User) => names.push({ id: user.id, name: `${user.first_name} ${user.last_name}` }));
        setUserOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsUserOptionsLoading(false));
  }, []);

  useEffect(() => {
    // Fetch person list
    getPersonList();

    // Fetch companies list
    getCompaniesList();

    // Fetch users list
    getUsersList();
  }, [getPersonList, getCompaniesList, getUsersList]);

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      // eslint-disable-next-line camelcase
      user_id: userChoice?.id || '',
      // eslint-disable-next-line camelcase
      person_id: personChoice?.id || '',
      // eslint-disable-next-line camelcase
      company_id: companyChoice?.id || '',
      notes: notesInput,
    };
    if (importer) {
      // Update existing importer
      axiosService
        .patch(`/entities/importer/${importer.id}/`, data)
        .then(importerData => {
          setLoading(false);
          setError(null);
          onSave(importerData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new importer
      axiosService
        .post('/entities/importer/', data)
        .then(importerData => {
          setLoading(false);
          setError(null);
          onSave(importerData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={!userChoice && (!personChoice || !companyChoice)}
        >
          {importer ? 'Edit importer' : 'Create importer'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {error ? <Typography>{error.error}</Typography> : null}
          <Autocomplete
            sx={{ mt: 1, mb: 2 }}
            value={userChoice}
            onChange={(event, value) => setUserChoice(value)}
            onInputChange={(event, value) => setUserTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={userOptions}
            loading={isUserOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="User"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewUserModalOpen(true)}>
                Add a new User
              </Button>
            }
          />
          {addNewUserModalOpen ? (
            <AddOrEditUser
              userName={userTextInput}
              onSave={newUser => {
                getUsersList(() => {
                  setUserChoice({
                    id: newUser.id,
                    name: `${newUser.first_name} ${newUser.last_name}`,
                  });
                  setAddNewUserModalOpen(false);
                });
              }}
              modalTitle="Add new user"
              isModalOpen={addNewUserModalOpen}
              setModalOpen={setAddNewUserModalOpen}
            />
          ) : null}
          <Autocomplete
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={personChoice}
            onChange={(event, value) => setPersonChoice(value)}
            onInputChange={(event, value) => setPersonTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={personOptions}
            loading={isPersonOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="Person"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewPersonModalOpen(true)}>
                Add a new person
              </Button>
            }
          />
          {addNewPersonModalOpen ? (
            <AddOrEditPeople
              personName={personTextInput}
              onSave={newPerson => {
                getPersonList(() => {
                  setPersonChoice({
                    id: newPerson.id,
                    name: `${newPerson.first_name} ${newPerson.last_name}`.trim(),
                  });
                  setAddNewPersonModalOpen(false);
                });
              }}
              modalTitle="Add new person"
              isModalOpen={addNewPersonModalOpen}
              setModalOpen={setAddNewPersonModalOpen}
            />
          ) : null}
          <Autocomplete
            fullWidth
            sx={{ mb: 1 }}
            value={companyChoice}
            onChange={(event, value) => setCompanyChoice(value)}
            onInputChange={(event, value) => setCompanyTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={companyOptions}
            loading={isCompanyOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="Company"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewCompanyModalOpen(true)}>
                Add a new Company
              </Button>
            }
          />
          {addNewCompanyModalOpen ? (
            <AddOrEditCompany
              companyName={companyTextInput}
              onSave={newCompany => {
                getCompaniesList(() => {
                  setCompanyChoice({
                    id: newCompany.id,
                    name: newCompany.name,
                  });
                  setAddNewCompanyModalOpen(false);
                });
              }}
              modalTitle="Add new company"
              isModalOpen={addNewCompanyModalOpen}
              setModalOpen={setAddNewCompanyModalOpen}
            />
          ) : null}
          <TextField
            fullWidth
            minRows={5}
            multiline
            margin="normal"
            id="notes"
            label="Notes"
            value={notesInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNotesInput(event.target.value);
            }}
          />
        </Box>
      </Box>
    </ModalPopup>
  );
}
